import { useState, useRef, forwardRef } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import useStyles from './styles';

function calculatePointsOffset(points) {
  const XList = points.map((point) => point[0]);
  const YList = points.map((point) => point[1]);
  const minY = Math.min(...YList);
  const minX = Math.min(...XList);
  if (minY < 0 && minX < 0) {
    return points.map((point) => [
      point[0] + Math.abs(minX),
      point[1] + Math.abs(minY),
    ]);
  }
  if (minY < 0) {
    return points.map((point) => [point[0], point[1] + Math.abs(minY)]);
  }
  if (minX < 0) {
    return points.map((point) => [point[0] + Math.abs(minX), point[1]]);
  }
  return points;
}

export const ImageMap = forwardRef((props, forwardedRef) => {
  const {
    src,
    map,
    onClick = () => {},
    onMouseEnter = () => {},
    onMouseLeave = () => {},
  } = props;

  const imgRef = useRef();
  const classes = useStyles();

  const [imageInfo, setImageInfo] = useState({});
  const {
    imageWidth, imageHeight, imageNatureWidth, imageNatureHeight,
  } = imageInfo;

  const getPolygonPoints = (area) => {
    const ratioByX = imageWidth / imageNatureWidth;
    const ratioByY = imageHeight / imageNatureHeight;

    const firstPoint = area.coords.reduce(
      (res, coord) => {
        if (res.x - coord.x > 2 && res.y - coord.y < 2) {
          return coord;
        }
        return res;
      },
      { ...area.coords[0] },
    );
    const coords = [...area.coords];
    const points = coords.map((coord) => {
      const x = coord.x - firstPoint.x;
      const y = coord.y - firstPoint.y;

      return [x * ratioByX, y * ratioByY];
    });
    const pointsWithOffset = calculatePointsOffset(points);
    return pointsWithOffset.join(', ');
  };

  const calculateRelativePositionStyles = (area) => {
    const {
      width, height, left, top,
    } = area.sizes;
    return {
      width: `${(width / imageNatureWidth) * 100}%`,
      height: `${(height / imageNatureHeight) * 100}%`,
      left: `${(left / imageNatureWidth) * 100}%`,
      top: `${(top / imageNatureHeight) * 100}%`,
    };
  };

  const calculateAreaCenter = (area) => {
    const {
      width, height, left, top,
    } = area.sizes;
    return {
      x:
        (left / imageNatureWidth) * 100
        + ((width / imageNatureWidth) * 100) / 2,
      y:
        (top / imageNatureHeight) * 100
        + ((height / imageNatureHeight) * 100) / 2,
    };
  };

  const handleImageLoad = () => {
    setImageInfo({
      imageWidth: (imgRef.current && imgRef.current.width) || 1,
      imageHeight: (imgRef.current && imgRef.current.height) || 1,
      imageNatureWidth: (imgRef.current && imgRef.current.naturalWidth) || 1,
      imageNatureHeight: (imgRef.current && imgRef.current.naturalHeight) || 1,
    });
  };

  return (
    <div ref={forwardedRef} className={classes.container}>
      <button
        type="button"
        className={classes.image}
        onClick={onClick}
        onLoad={handleImageLoad}
      >
        <img alt="deck" ref={imgRef} className={classes.image} src={src} />
      </button>

      {map.areas.map((area, index) => (
        <svg
          key={`${index.toString()}`}
          className={cn(classes.area, {
            [classes.pulseAnimate]: area.isAvailable && !area.isSelectedCabin,
            [classes.selectedArea]: area.isSelectedCabin,
          })}
          style={calculateRelativePositionStyles(area)}
        >
          <polygon
            fill={area.preFillColor}
            points={getPolygonPoints(area)}
            onClick={() => onClick(area)}
            onMouseEnter={() => onMouseEnter(area, calculateAreaCenter(area))}
            onMouseLeave={() => onMouseLeave(area)}
          />
        </svg>
      ))}
    </div>
  );
});

ImageMap.displayName = 'ImageMap';

ImageMap.propTypes = {
  src: PropTypes.string.isRequired,
  map: PropTypes.shape({
    areas: PropTypes.arrayOf(
      PropTypes.shape({
        coords: PropTypes.arrayOf(PropTypes.number),
        isAvailable: PropTypes.bool,
        isSelectedCabin: PropTypes.bool,
        preFillColor: PropTypes.string,
        selectedCabinStateroom: PropTypes.number,
        shape: PropTypes.string,
        sizes: PropTypes.objectOf(PropTypes.number),
        title: PropTypes.string,
      }),
    ),
    name: PropTypes.string,
  }),
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
};

ImageMap.defaultProps = {
  map: {
    areas: [],
    name: '',
  },
  onClick: () => {},
  onMouseEnter: () => {},
  onMouseLeave: () => {},
};

export default ImageMap;
